import React from 'react'
import { HeaderBlock, useAtSize, Button, useOverlay, InputRow } from '@fs/zion-ui'
import { ThingCalendar, PlaceWorld } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import Tile from './Tile'
import YearPicker from './YearPicker/YearPicker'
import { useDSCState } from '../../../state/DSCStore'
import Masonry from './Masonry'
import AAMCountrySelector from './AAMCountrySelector'
import { useCorrections } from '../hooks/hooks'

const colorList = [
  {
    // Light Green
    background: '#bed21e',
  },
  {
    // Green
    background: '#6db344',
  },
  {
    // Blue
    background: '#01b6d1',
  },
  {
    // Light Blue
    background: '#7de3f4',
  },
  {
    // Light Orange / Yellow
    background: '#ffb81c',
  },
  {
    // Orange
    background: '#f68d2e',
  },
  {
    // Pink
    background: '#fc4e6d',
  },
]

const urlParams = new URLSearchParams(window.location.search)
const collection = urlParams.get('collection')
const experience = urlParams.get('experience')

const collections = {
  names: ['NAME MEANING 4', 'NAME POPULARITY 28', 'MY HERITAGE 27'],
  year: [
    'POPULATION QUIZ 1',
    'COST COMPARISON 2',
    'MOVIE TICKET 3',
    'SPORTS TRIVIA 5',
    'SPORTS TRIVIA 6',
    'SPORTS TRIVIA 7',
    'SPORTS TRIVIA 8',
    'SPORTS TRIVIA 9',
    'SCIENCE AND TECHNOLOGY 10',
    'COST COMPARISON 11',
    'TENNIS 12',
    'TOP STORIES 13',
    'US HISTORY QUIZ 14',
    'POPULATION QUIZ 15',
    'SPORTS TRIVIA 17',
    'CHURCH HISTORY QUIZ 29',
    'TEMPLE DEDICATIONS 30',
    'CHURCH HISTORY QUIZ 31',
    'TOP GROSSING FILM 33',
    'POPULAR SONG 36',
    'SONG OF THE YEAR 37',
    'ALBUM OF THE YEAR 38',
  ],
}

const getCardColors = (index) => {
  const selectedColors = {
    background: '#DADADA',
    title: 'rgba(0,0,0,0.4)',
    text: '#000',
    icon: '#fff',
  }

  // cycle through the list of global default colors

  let i = (index % colorList.length) - 1
  if (i === -1) {
    i = colorList.length - 1
  }

  selectedColors.background = colorList[i].background || '#DADADA'
  selectedColors.title = colorList[i].title || 'rgba(0,0,0,0.4)'
  selectedColors.text = colorList[i].text || '#000'
  selectedColors.icon = colorList[i].icon || '#fff'

  return selectedColors
}

export default function AAMContent({ tileData, year, user }) {
  const [t] = useTranslation()
  const atSize = useAtSize()
  const countryOverlay = useOverlay()
  const overlay = useOverlay()
  const isCorrections = useCorrections()
  const exitCorrections = () => {
    sessionStorage.removeItem('FS.ds.nonstandardUser')
    window.location.href = '/discovery/about/correctionslogin'
  }
  const { isOpen } = overlay
  const { isOpen: isCountryOpen } = countryOverlay

  const dscState = useDSCState()

  const rootContainerStyle = css`
    max-width: 100%;
    max-height: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
    background-color: #fff;
    margin: 10px;
  `
  const masonryWrapperCSS = css`
    margin: ${atSize({ default: '0 10px 0 10px', lg: '0 30px 0 30px' })};
  `
  const topStyle = css`
    display: flex;
    justify-content: space-between;
    flex-direction: ${atSize({ default: 'column', lg: 'row' })};
    flex-wrap: wrap;
    margin: 60px 30px 30px 30px;
    ${atSize({ default: 'align-items: center', md: null })};
    button {
      align-self: ${atSize({ default: 'flex-start', md: 'center' })};
    }
    header {
      margin: ${atSize({ default: '10px 0', lg: '0' })};
    }
  `

  let yearHeader = t('aam.heres-what-was-happening', `Here's what was happening in`)
  yearHeader = `${yearHeader} ${year}`

  const columnCount = atSize({ default: 1, sm: 1, lg: 2, xl: 3, xxl: 4 })
  const country = t(`global.countries.country${tileData?.[0]?.region.toUpperCase()}`)
  const language = t(`global.countries.language.${tileData?.[0]?.language}`)

  return (
    <div css={rootContainerStyle}>
      <div css={topStyle}>
        {user?.signedIn && (
          <Button Icon={PlaceWorld} emphasis="low" data-testid="Country" onClick={countryOverlay.handleClick}>
            {tileData[0] ? `${country} - ${language}` : `${t('aam.content.country.loading', 'Loading...')}`}
          </Button>
        )}
        <HeaderBlock size={atSize({ default: 'md', lg: 'lg' })} heading={yearHeader} />
        <InputRow>
          <Button Icon={ThingCalendar} emphasis="billboard" data-testid="Year" onClick={overlay.open}>
            &nbsp;{t('aam.view-different-year', 'View a different year')}
          </Button>
          {isCorrections && (
            <Button emphasis="billboard" onClick={exitCorrections}>
              &nbsp;{t('aam.exit', 'Exit')}
            </Button>
          )}
        </InputRow>
      </div>

      {isCountryOpen && <AAMCountrySelector overlay={countryOverlay} />}

      {isOpen && (
        <YearPicker
          overlay={overlay}
          tileData={tileData}
          showAncestorList={
            (dscState.nonstandardUser.initialized && dscState.nonstandardUser.authenticated) || user?.signedIn
          }
        />
      )}

      <div css={masonryWrapperCSS}>
        <Masonry gap="20px" columnCount={columnCount}>
          {tileData &&
            tileData
              .filter((tile) =>
                collection
                  ? collections[collection] && collections[collection].indexOf(`${tile.title} ${tile.id}`) > -1
                  : true
              )
              .map((tile, indx) => (
                <Tile
                  autoload={`${tile.title} ${tile.id}` === experience && experience !== 'ABOUT YOUR ANCESTOR 39'}
                  tileData={tile}
                  key={tile.id}
                  color={getCardColors(indx).background}
                  index={indx}
                />
              ))}
        </Masonry>
      </div>
    </div>
  )
}
